import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import aiService from "../services/aiService";
import jobService from "../services/jobService";
import { useAuth } from "./AuthContext";

// Create the context with an initial value (null or a default shape)
const AIWorkflowContext = createContext(null);

// Custom hook to use the AI Workflow context
export const useAIWorkflow = () => {
  const context = useContext(AIWorkflowContext);
  if (!context) {
    throw new Error("useAIWorkflow must be used within an AIWorkflowProvider");
  }
  return context;
};

// The Provider component
export const AIWorkflowProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth(); // Get auth info

  // --- State Definitions ---
  const [aiSessionId, setAiSessionId] = useState(null);
  const [aiTaskId, setAiTaskId] = useState(null); // Task ID for job polling
  const [profileInputText, setProfileInputText] = useState(null); // text/content used for LAST analysis
  const [userLocationInput, setUserLocationInput] = useState("Canada"); // Default or user input
  const [jobCategories, setJobCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryError, setCategoryError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [masterResume, setMasterResume] = useState(null); // AI generated HTML content
  const [isLoadingMasterResume, setIsLoadingMasterResume] = useState(false);
  const [masterResumeError, setMasterResumeError] = useState(null);
  const [structuredResumeData, setStructuredResumeData] = useState(null);
  const [isLoadingPremiumResume, setIsLoadingPremiumResume] = useState(false);
  const [premiumResumeError, setPremiumResumeError] = useState(null);
  const [aiMatchedJobs, setAiMatchedJobs] = useState([]); // Jobs found by AI polling
  const [isLoadingAiJobs, setIsLoadingAiJobs] = useState(false); // Loading state specifically for AI job polling
  const [aiJobsError, setAiJobsError] = useState(null); // Error during AI job polling
  const [pollingForJobs, setPollingForJobs] = useState(false); // Flag if job polling is active
  const [selectedJobForOptimization, setSelectedJobForOptimization] =
    useState(null); // Job selected for tailoring
  const [optimizedResume, setOptimizedResume] = useState(null); // Tailored resume content (HTML/text)
  const [isLoadingOptimizedResume, setIsLoadingOptimizedResume] =
    useState(false);
  const [optimizeResumeError, setOptimizeResumeError] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null); // Generated cover letter content
  const [isLoadingCoverLetter, setIsLoadingCoverLetter] = useState(false);
  const [coverLetterError, setCoverLetterError] = useState(null);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState(null);
  const [resumeSourceForOptimization, setResumeSourceForOptimization] =
    useState("generated");
  const [
    finalResumeContentForJobSearch,
    setFinalResumeContentForJobSearchState,
  ] = useState(null);
  const [resumeListVersion, setResumeListVersion] = useState(0);
  const [originalSourceInfo, setOriginalSourceInfo] = useState(null);
  const [flowShouldSkipResumeBuilder, setFlowShouldSkipResumeBuilder] =
    useState(false);

  // --- *** NEW: State for pagination information *** ---
  const [paginationInfo, setPaginationInfo] = useState(null);

  // --- Helper Actions ---
  const incrementResumeListVersion = useCallback(() => {
    console.log("[AIContext] Incrementing resume list version.");
    setResumeListVersion((v) => v + 1);
  }, []);

  // --- Core Workflow Actions ---

  // --- *** MODIFIED: This function now handles the paginated response *** ---
  const pollForAiJobs = useCallback(
    async (taskId) => {
      if (!taskId) return;
      if (pollingForJobs) return;

      setPollingForJobs(true);
      setIsLoadingAiJobs(true);
      setAiJobsError(null);
      setAiMatchedJobs([]);
      setPaginationInfo(null); // Reset pagination on new poll

      try {
        // The service now returns an object: { jobs, pagination }
        const result = await jobService.pollJobMatchResults(taskId);

        if (result && result.jobs) {
          setAiMatchedJobs(result.jobs || []);
          setPaginationInfo(result.pagination || null); // Store the pagination info
        } else {
          // Fallback if the result is not as expected
          setAiMatchedJobs([]);
          setPaginationInfo(null);
        }
      } catch (err) {
        setAiJobsError(
          err?.message || `Job polling failed for task ${taskId}.`
        );
        setAiMatchedJobs([]);
        setPaginationInfo(null); // Ensure pagination is cleared on error
      } finally {
        setIsLoadingAiJobs(false);
        setPollingForJobs(false);
      }
    },
    [pollingForJobs]
  );

  // --- NEW: Function to search jobs for a specific category ---
  const searchJobsForCategory = useCallback(
    async (category, location) => {
      if (!category) {
        setAiJobsError("No category selected for job search.");
        return false;
      }

      if (!aiTaskId || !aiSessionId) {
        setAiJobsError("No active AI session. Please start a new analysis.");
        return false;
      }

      console.log("[AIWorkflowContext] searchJobsForCategory called with:", {
        category: category,
        categoryTitle: category.job_category,
        categoryId: category.id,
        location: location || userLocationInput,
        aiTaskId: aiTaskId,
        aiSessionId: aiSessionId,
      });

      // Reset job-related state before searching
      setAiMatchedJobs([]);
      setAiJobsError(null);
      setPaginationInfo(null);
      setIsLoadingAiJobs(true);

      try {
        // Call the new backend endpoint
        const response = await jobService.searchJobsByCategory({
          session_id: aiSessionId,
          task_id: aiTaskId,
          category_title: category.job_category,
          location: location || userLocationInput,
          match_percentage: category.match_percentage,
        });

        console.log("[AIWorkflowContext] Category search response:", response);

        if (response?.task_id) {
          // Store the new task ID for this category-specific search
          const categoryTaskId = response.task_id;

          // Poll for results using the new task ID
          await pollForAiJobs(categoryTaskId);
          return true;
        } else {
          throw new Error("No task ID returned from category search");
        }
      } catch (err) {
        console.error("[AIWorkflowContext] Job search error:", err);
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to search for jobs.";
        setAiJobsError(message);
        return false;
      } finally {
        setIsLoadingAiJobs(false);
      }
    },
    [aiTaskId, aiSessionId, userLocationInput, pollForAiJobs]
  );

  const startAnalysisAndGetCategories = useCallback(
    async (textForAnalysis, location, sourceInfo) => {
      if (!currentUser?.email) {
        setCategoryError("Authentication required to start analysis.");
        return false;
      }
      if (!location?.trim()) {
        setCategoryError("Location is required for analysis.");
        return false;
      }
      if (
        sourceInfo?.type === "selected_resume_file" &&
        (!textForAnalysis || textForAnalysis.trim().length < 50)
      ) {
        setCategoryError(
          "Selected resume content is too short (min 50 chars). Analysis cannot proceed."
        );
        return false;
      }

      setIsLoadingCategories(true);
      setCategoryError(null);

      // --- RESET ALL RELEVANT WORKFLOW STATE ---
      setJobCategories([]);
      setSelectedCategory(null);
      setMasterResume(null);
      setMasterResumeError(null);
      setStructuredResumeData(null);
      setPremiumResumeError(null);
      setAiMatchedJobs([]);
      setAiJobsError(null);
      setPollingForJobs(false);
      setPaginationInfo(null); // --- *** NEW: Reset pagination info *** ---
      setSelectedJobForOptimization(null);
      setOptimizedResume(null);
      setOptimizeResumeError(null);
      setCoverLetter(null);
      setCoverLetterError(null);
      setAiSessionId(null);
      setAiTaskId(null);
      setIsLoadingMasterResume(false);
      setIsLoadingPremiumResume(false);
      setIsLoadingAiJobs(false);
      setIsLoadingOptimizedResume(false);
      setIsLoadingCoverLetter(false);
      setRecommendedCourses([]);
      setCoursesError(null);
      setIsLoadingCourses(false);
      setResumeSourceForOptimization("generated");
      setFinalResumeContentForJobSearchState(null);
      setOriginalSourceInfo(null);
      setProfileInputText(null);
      // --- END RESET ---

      setFlowShouldSkipResumeBuilder(!!sourceInfo?.skipResumeBuilderIntent);
      const fullSourceInfo = {
        ...sourceInfo,
        profileInputText: textForAnalysis,
      };
      setOriginalSourceInfo(fullSourceInfo);
      setProfileInputText(textForAnalysis);
      setUserLocationInput(location.trim());

      try {
        const result = await aiService.getJobCategoriesAndStartSearch(
          currentUser.email,
          textForAnalysis,
          location.trim()
        );
        if (result?.categories && result?.task_id && result?.session_id) {
          setJobCategories(result.categories);
          setAiTaskId(result.task_id);
          setAiSessionId(result.session_id);
          if (
            sourceInfo?.skipResumeBuilderIntent &&
            sourceInfo?.type === "selected_resume_file"
          ) {
            setFinalResumeContentForJobSearchState(textForAnalysis);
            setMasterResume(null);
            setSelectedCategory(null);
            setResumeSourceForOptimization("selected_resume_file");
          }

          // REMOVED: Automatic job polling
          // Job polling will now be triggered manually by the user

          return true;
        } else {
          throw new Error(
            result?.detail || "Invalid response from analysis service."
          );
        }
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to start analysis and get job categories.";
        setCategoryError(message);
        setAiSessionId(null);
        setAiTaskId(null);
        setOriginalSourceInfo(null);
        setFlowShouldSkipResumeBuilder(false);
        setFinalResumeContentForJobSearchState(null);
        return false;
      } finally {
        setIsLoadingCategories(false);
      }
    },
    [currentUser]
  );

  const setFinalResumeContentForJobSearch = useCallback((content) => {
    setFinalResumeContentForJobSearchState(content);
  }, []);

  const generateMasterResumeAction = useCallback(async () => {
    if (!aiSessionId) {
      setMasterResumeError(
        "AI session not found. Cannot generate master resume."
      );
      return false;
    }
    if (flowShouldSkipResumeBuilder) {
      setMasterResumeError(
        "Master resume generation is skipped for the current workflow path."
      );
      return false;
    }
    setIsLoadingMasterResume(true);
    setMasterResumeError(null);
    setMasterResume(null);
    try {
      const result = await aiService.generateMasterResume(aiSessionId);
      if (result?.master_resume) {
        setMasterResume(result.master_resume);
        return true;
      } else {
        throw new Error(
          result?.detail || "Master resume content not found in API response."
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to generate master resume.";
      setMasterResumeError(message);
      return false;
    } finally {
      setIsLoadingMasterResume(false);
    }
  }, [aiSessionId, flowShouldSkipResumeBuilder]);

  const generatePremiumResumeAction = useCallback(async () => {
    if (!aiSessionId) {
      setPremiumResumeError(
        "AI session not found. Cannot generate premium resume."
      );
      return false;
    }
    setIsLoadingPremiumResume(true);
    setPremiumResumeError(null);
    setStructuredResumeData(null);
    try {
      const result = await aiService.generateStructuredResume(aiSessionId);
      if (result?.structured_resume) {
        setStructuredResumeData(result.structured_resume);
        return true;
      } else {
        throw new Error(
          result?.detail || "Structured resume data not found in API response."
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to generate structured premium resume.";
      setPremiumResumeError(message);
      return false;
    } finally {
      setIsLoadingPremiumResume(false);
    }
  }, [aiSessionId]);

  const optimizeResumeAction = useCallback(async () => {
    const baseResumeContent = finalResumeContentForJobSearch;

    console.log("[AIWorkflowContext] optimizeResumeAction called");
    console.log("[AIWorkflowContext] finalResumeContentForJobSearch:", {
      value: baseResumeContent,
      type: typeof baseResumeContent,
      length: baseResumeContent?.length,
      isNull: baseResumeContent === null,
      isUndefined: baseResumeContent === undefined,
      isEmpty: baseResumeContent === "",
    });

    if (!selectedJobForOptimization) {
      setOptimizeResumeError("No job selected for optimization.");
      return false;
    }

    if (!aiSessionId) {
      setOptimizeResumeError("AI session not found. Cannot optimize resume.");
      return false;
    }

    console.log("[AIWorkflowContext] Session ID:", aiSessionId);
    console.log(
      "[AIWorkflowContext] Selected job:",
      selectedJobForOptimization
    );

    const jobDetails = selectedJobForOptimization;
    const jobDetailsText = `Job Title: ${
      jobDetails.jobTitle || "N/A"
    }\nCompany: ${jobDetails.companyName || "N/A"}\nLocation: ${
      jobDetails.location || "N/A"
    }\n\nDescription:\n${
      jobDetails.description || "No job description available."
    }`;

    setIsLoadingOptimizedResume(true);
    setOptimizeResumeError(null);
    setOptimizedResume(null);
    setCoverLetter(null);
    setCoverLetterError(null);

    try {
      console.log(
        "[AIWorkflowContext] Calling aiService.optimizeResumeForJob with:",
        {
          resumeContent: baseResumeContent
            ? `${baseResumeContent.length} chars`
            : "null/empty",
          jobDetailsLength: jobDetailsText.length,
          sessionId: aiSessionId,
        }
      );

      const result = await aiService.optimizeResumeForJob(
        baseResumeContent, // This can be null, empty string, or actual content
        jobDetailsText,
        aiSessionId
      );

      console.log("[AIWorkflowContext] Optimization result received:", {
        hasResult: !!result,
        hasOptimizedResume: !!result?.optimized_resume,
        resultKeys: result ? Object.keys(result) : [],
      });

      if (result?.optimized_resume) {
        setOptimizedResume(result.optimized_resume);
        return true;
      } else {
        throw new Error(
          result?.detail ||
            "Optimized resume content not found in API response."
        );
      }
    } catch (err) {
      console.error("[AIWorkflowContext] Optimization error:", err);
      console.error("[AIWorkflowContext] Error response:", err?.response?.data);

      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to optimize resume.";
      setOptimizeResumeError(message);
      return false;
    } finally {
      setIsLoadingOptimizedResume(false);
    }
  }, [selectedJobForOptimization, aiSessionId, finalResumeContentForJobSearch]);

  const generateCoverLetterAction = useCallback(async () => {
    if (!selectedJobForOptimization) {
      setCoverLetterError("No job selected. Cannot generate cover letter.");
      return false;
    }
    if (!optimizedResume) {
      setCoverLetterError(
        "Optimized resume is required to generate a cover letter."
      );
      return false;
    }
    if (!aiSessionId) {
      setCoverLetterError(
        "AI session not found. Cannot generate cover letter."
      );
      return false;
    }
    const jobDetails = selectedJobForOptimization;
    const jobDetailsText = `Job Title: ${
      jobDetails.jobTitle || "N/A"
    }\nCompany: ${jobDetails.companyName || "N/A"}\nLocation: ${
      jobDetails.location || "N/A"
    }\n\nDescription:\n${
      jobDetails.description || "No job description available."
    }`;
    setIsLoadingCoverLetter(true);
    setCoverLetterError(null);
    setCoverLetter(null);
    try {
      const result = await aiService.generateCoverLetter(
        optimizedResume,
        jobDetailsText,
        aiSessionId
      );
      if (result?.cover_letter) {
        setCoverLetter(result.cover_letter);
        return true;
      } else {
        throw new Error(
          result?.detail || "Cover letter content not found in API response."
        );
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to generate cover letter.";
      setCoverLetterError(message);
      return false;
    } finally {
      setIsLoadingCoverLetter(false);
    }
  }, [selectedJobForOptimization, optimizedResume, aiSessionId]);

  const fetchCourseRecommendations = useCallback(async () => {
    if (!aiSessionId) {
      setCoursesError("AI session required for course recommendations.");
      return;
    }
    setIsLoadingCourses(true);
    setCoursesError(null);
    setRecommendedCourses([]);
    try {
      const courses = await aiService.getCourseRecommendations(aiSessionId);
      if (Array.isArray(courses)) {
        setRecommendedCourses(courses);
      } else {
        throw new Error("Invalid data format for course recommendations.");
      }
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to fetch course recommendations.";
      setCoursesError(message);
      setRecommendedCourses([]);
    } finally {
      setIsLoadingCourses(false);
    }
  }, [aiSessionId]);

  const resetWorkflow = useCallback(() => {
    setProfileInputText(null);
    setUserLocationInput("Canada");
    setJobCategories([]);
    setCategoryError(null);
    setSelectedCategory(null);
    setMasterResume(null);
    setMasterResumeError(null);
    setAiMatchedJobs([]);
    setAiJobsError(null);
    setPollingForJobs(false);
    setPaginationInfo(null); // --- *** NEW: Reset pagination info *** ---
    setSelectedJobForOptimization(null);
    setOptimizedResume(null);
    setOptimizeResumeError(null);
    setCoverLetter(null);
    setCoverLetterError(null);
    setAiSessionId(null);
    setAiTaskId(null);
    setIsLoadingCategories(false);
    setIsLoadingMasterResume(false);
    setIsLoadingAiJobs(false);
    setIsLoadingOptimizedResume(false);
    setIsLoadingCoverLetter(false);
    setRecommendedCourses([]);
    setCoursesError(null);
    setIsLoadingCourses(false);
    setResumeSourceForOptimization("generated");
    setFinalResumeContentForJobSearchState(null);
    setOriginalSourceInfo(null);
    setFlowShouldSkipResumeBuilder(false);
    setStructuredResumeData(null);
    setIsLoadingPremiumResume(false);
    setPremiumResumeError(null);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      resetWorkflow();
    }
  }, [isAuthenticated, resetWorkflow]);

  // --- *** MODIFIED: Context value now exposes new state and setters *** ---
  const value = useMemo(
    () => ({
      // State
      aiSessionId,
      aiTaskId,
      profileInputText,
      userLocationInput,
      jobCategories,
      selectedCategory,
      masterResume,
      aiMatchedJobs,
      paginationInfo, // --- NEW: Expose pagination info ---
      selectedJobForOptimization,
      optimizedResume,
      coverLetter,
      recommendedCourses,
      resumeSourceForOptimization,
      finalResumeContentForJobSearch,
      resumeListVersion,
      originalSourceInfo,
      flowShouldSkipResumeBuilder,
      structuredResumeData,

      // Loading States
      isLoadingCategories,
      isLoadingMasterResume,
      isLoadingAiJobs,
      pollingForJobs,
      isLoadingOptimizedResume,
      isLoadingCoverLetter,
      isLoadingCourses,
      isLoadingPremiumResume,

      // Error States
      categoryError,
      masterResumeError,
      aiJobsError,
      optimizeResumeError,
      coverLetterError,
      coursesError,
      premiumResumeError,

      // Actions / Setters
      setProfileInputText,
      setUserLocationInput,
      setSelectedCategory,
      setSelectedJobForOptimization,
      setResumeSourceForOptimization,
      setFinalResumeContentForJobSearch,
      setAiMatchedJobs, // --- NEW: Expose setter for jobs (for appending) ---
      setPaginationInfo, // --- NEW: Expose setter for pagination ---
      setAiTaskId, // --- ADD THIS: Needed for ResumeBuilderPage ---
      setAiSessionId, // --- ADD THIS: Needed for ResumeBuilderPage ---
      setOptimizedResume, // --- ADD THIS: Expose setter for optimized resume ---
      pollForAiJobs, // --- ADD THIS: Needed for ResumeBuilderPage ---
      startAnalysisAndGetCategories,
      generateMasterResumeAction,
      optimizeResumeAction,
      generateCoverLetterAction,
      resetWorkflow,
      fetchCourseRecommendations,
      incrementResumeListVersion,
      generatePremiumResumeAction,
      setStructuredResumeData,
      searchJobsForCategory, // --- NEW: Expose the job search function ---
    }),
    [
      // Add ALL provided values and their setters to the dependency array
      aiSessionId,
      aiTaskId,
      profileInputText,
      userLocationInput,
      jobCategories,
      selectedCategory,
      masterResume,
      aiMatchedJobs,
      paginationInfo, // NEW
      selectedJobForOptimization,
      optimizedResume,
      coverLetter,
      recommendedCourses,
      resumeSourceForOptimization,
      finalResumeContentForJobSearch,
      resumeListVersion,
      originalSourceInfo,
      flowShouldSkipResumeBuilder,
      structuredResumeData,
      isLoadingCategories,
      isLoadingMasterResume,
      isLoadingAiJobs,
      pollingForJobs,
      isLoadingOptimizedResume,
      isLoadingCoverLetter,
      isLoadingCourses,
      isLoadingPremiumResume,
      categoryError,
      masterResumeError,
      aiJobsError,
      optimizeResumeError,
      coverLetterError,
      coursesError,
      premiumResumeError,
      setProfileInputText,
      setUserLocationInput,
      setSelectedCategory,
      setSelectedJobForOptimization,
      setResumeSourceForOptimization,
      setFinalResumeContentForJobSearch,
      setAiMatchedJobs,
      setPaginationInfo, // NEW
      setAiTaskId, // ADD TO DEPENDENCIES
      setAiSessionId, // ADD TO DEPENDENCIES
      setOptimizedResume, // ADD TO DEPENDENCIES
      pollForAiJobs, // ADD TO DEPENDENCIES
      startAnalysisAndGetCategories,
      generateMasterResumeAction,
      optimizeResumeAction,
      generateCoverLetterAction,
      resetWorkflow,
      fetchCourseRecommendations,
      incrementResumeListVersion,
      generatePremiumResumeAction,
      setStructuredResumeData,
      searchJobsForCategory, // NEW
    ]
  );

  return (
    <AIWorkflowContext.Provider value={value}>
      {children}
    </AIWorkflowContext.Provider>
  );
};
